import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { HttpClient, HttpHandler, HttpHeaders } from '@angular/common/http';
import { ConditionalRowDetailsDTO, MaintainTabDTO, MenuDTO, TickerCommandDTO } from '../../../../../system/system-dtos';
import { EnvironmentService } from '../../../../../common/services/env-configuration/environment.service';
import { MaintainService } from '../../maintain.service';
import { RowActionsService } from './row-actions.service';
import { DrawLottolandComplianceComponent } from '../../../../dialogs/draw-lottoland-compliance/draw-lottoland-compliance.component';
import { DataSource } from '../../data-source/data-source';
import { ExtractedActions } from '../../../../../common/event-dtos';
import { SignInService } from '../../../../../common/services/sign-in/sign-in.service';
import { DataSourceFactory } from '../../data-source/data-source-factory';
import { ActionService } from '../../../../action/action.service';
import { MenuUtils } from '../../../../../common/utils/menu-utils';
import { LottoUser } from '../../../sign-in/user.model';

@Component({
  selector: 'app-maintain-table-row-actions',
  templateUrl: './maintain-table-row-actions.component.html',
  styleUrls: ['./maintain-table-row-actions.component.scss'],
  standalone: false,
})
export class MaintainTableRowActionsComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() menuConf: MenuDTO;

  @Input() currentTab: MaintainTabDTO;

  @Input() code: string;

  @Input() public id: string;

  @Input() detailsMode = false;

  @Input() conditional: ConditionalRowDetailsDTO;

  @Output() created: EventEmitter<MaintainTableRowActionsComponent> =
    new EventEmitter<MaintainTableRowActionsComponent>();

  @Output() onActionStart = new EventEmitter<void>();

  @Output() onResponse = new EventEmitter<any>();

  _data: any;

  private currentUser: LottoUser;

  private subscription: Subscription;

  @Input()
  set data(data: any) {
    this._data = data;
    if (this.dataSource) {
      this.extractedActions = this.dataSource.extractActions(this.menuConf, this.currentTab, this._data);
    }
  }

  actionInProgress = false;

  dataSource: DataSource;

  private map = {
    edit: {
      icon: 'edit',
      action: (tag) => this.router.navigate([`${this.getEntityUrl()}/edit`], { relativeTo: this.route.parent }),
    },
    view: {
      icon: 'search',
      action: (tag) => this.router.navigate([this.getEntityUrl()], { relativeTo: this.route.parent }),
    },
    select: {
      icon: 'search',
      action: (tag) => this.router.navigate([this.getEntityUrl()], { relativeTo: this.route.parent }),
    },
    delete: {
      icon: 'delete',
      action: (tag) => this.delete(tag),
    },
    'add-draw': {
      icon: 'add',
      action: (tag) => {
        const dialogRef = this.dialog.open(DrawLottolandComplianceComponent, {
          maxWidth: '90vw',
          maxHeight: '90vh',
          data: { lottoId: this._data.id },
        });
      },
    },
  };

  public extractedActions: ExtractedActions;

  anyActions: boolean;

  iconButtons: TickerCommandDTO[] = [];

  constructor(
    private router: Router,
    private envService: EnvironmentService,
    private maintainService: MaintainService,
    private service: RowActionsService,
    private dialog: MatDialog,
    private toastrService: ToastrService,
    private dataSourceFactory: DataSourceFactory,
    public signInService: SignInService,
    public changeDetectorRef: ChangeDetectorRef,
    private ngZone: NgZone,
    private actionService: ActionService,
    private http: HttpClient,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.initialize();
    this.signInService.initiatedUser.subscribe(() => {
      this.initialize();
    });
    this.subscription = this.maintainService.rowActionsChangeSubscriber.subscribe((d) => this.initialize());
  }

  private initialize() {
    if (this.detailsMode) {
      this.currentTab = MenuUtils.getFirstMaintainConfig(this.menuConf.entityGroup).tabs[0];
    }
    this.dataSource = this.dataSourceFactory.get(this.menuConf.menuType);
    this.extractedActions = this.dataSource.extractActions(this.menuConf, this.currentTab, this._data);
    this.anyActions =
      (this.extractedActions.simple && this.extractedActions.simple.length > 0) ||
      (this.extractedActions.rest &&
        this.extractedActions.rest.filter((a) => !this.actionService.determineIsDisabled(this._data, a.jsEditCondition))
          .length > 0);

    this.currentUser = this.signInService.getCurrentUser();
    this.determineQuickAccess();
  }

  private determineQuickAccess() {
    const tickerConf = MenuUtils.getFirstTickerConfig(this.menuConf.entityGroup);

    this.iconButtons = !tickerConf ? [] : tickerConf.commands.filter((c) => this.isIconButtonAvailable(c));
  }

  private isIconButtonAvailable(c) {
    return (
      c.icon &&
      c.quickAccess &&
      ((this.extractedActions.simple && this.extractedActions.simple.find((a) => a.command === c.command)) ||
        (this.extractedActions.rest && this.extractedActions.rest.find((r) => r.buttonLabel === c.buttonLabel)))
    );
  }

  public getEntityUrl() {
    return `./${this.code}/${this.maintainService.createAppIdForEntity(this.menuConf, this._data)}`;
  }

  parseTag(tag: string): string {
    return tag.charAt(0).toUpperCase() + tag.slice(1).replace('-', ' ');
  }

  invokeAction(tag: string): void {
    const action = this.map[tag];

    if (action) {
      action.action(tag);
    }
  }

  public lock() {
    this.map['lock'].action('lock');
  }

  public unlock() {
    this.map['unlock'].action('unlock');
  }

  ngAfterViewInit(): void {
    this.created.emit(this);
  }

  dataUpdated(find: any) {
    this.extractedActions = this.dataSource.extractActions(this.menuConf, this.currentTab, this._data);
  }

  getCurrentUserEmail() {
    return this.currentUser ? this.currentUser.email : null;
  }

  detailsNavigation() {
    return this.conditional && this.conditional.detailsButton;
  }

  getDetailsLink() {
    return `${this.router.url.split('?')[0]}/${this._data.id}`;
  }

  actionStarted() {
    this.onActionStart.emit();
  }

  actionFinished(responseContent: any) {
    this.onResponse.emit(responseContent);
  }

  ngOnDestroy(): void {
    if (this.subscription && !this.subscription.closed) {
      this.subscription.unsubscribe();
    }
  }

  delete(tag: any): void {
    const { id } = this._data;
    this.http.post(`${this.getEntityUrl()}/delete`, this._data).subscribe((d) => {
      this.actionFinished(d);
      this.toastrService.success(`id ${id} deleted.`);
    });
  }
}
